import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import extractText from 'flarum/common/utils/extractText';
const t = (key, params) => params ? app.translator.trans(`ernestdefoe-page-builder.${key}`, params) : app.translator.trans(`ernestdefoe-page-builder.${key}`);

/** Normalize whatever is stored into an { url, ... } object (or null). */
function asImage(value) {
  if (!value) return null;
  if (typeof value === 'string') return value ? {
    url: value
  } : null;
  if (typeof value === 'object' && value.url) return value;
  return null;
}

/**
 * Field input for the `image` type. Uploads through fof/upload
 * (POST /api/fof/upload, multipart `files[]`) and stores the returned URL.
 * Supports click-to-pick and drag-and-drop, with a live preview + remove.
 */
export default class ImageUploadInput extends Component {
  uploading = false;
  dragging = false;
  error = null;
  broken = false; // the current image URL failed to load

  view() {
    const img = asImage(this.attrs.value);
    const urlValue = typeof this.attrs.value === 'string' ? this.attrs.value : this.attrs.value?.url || '';
    const showPreview = img && !this.broken;
    return m("div", {
      className: "PB-ImageField"
    }, m("label", {
      className: `PB-ImageField-drop ${this.dragging ? 'is-dragging' : ''} ${this.uploading ? 'is-uploading' : ''} ${showPreview ? 'has-preview' : ''}`,
      ondragover: e => {
        e.preventDefault();
        this.dragging = true;
      },
      ondragleave: () => {
        this.dragging = false;
      },
      ondrop: e => {
        e.preventDefault();
        this.dragging = false;
        const file = e.dataTransfer?.files?.[0];
        if (file) this.upload(file);
      }
    }, showPreview ? m("img", {
      className: "PB-ImageField-previewImg",
      src: img.thumbnailUrl || img.url,
      alt: img.alt || '',
      onerror: () => {
        if (!this.broken) {
          this.broken = true;
          m.redraw();
        }
      }
    }) : this.uploading ? m("span", {
      className: "PB-ImageField-status"
    }, m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }), " ", t('forum.image.uploading')) : m("span", {
      className: "PB-ImageField-status"
    }, m("i", {
      className: `fas ${img ? 'fa-triangle-exclamation' : 'fa-cloud-arrow-up'}`
    }), img ? m("span", null, t('forum.image.broken')) : m("span", null, m("strong", null, t('forum.image.click_to_upload')), " ", t('forum.image.drag_drop'))), m("input", {
      type: "file",
      accept: "image/*",
      disabled: this.uploading,
      onchange: e => {
        const file = e.target.files?.[0];
        if (file) this.upload(file);
        e.target.value = '';
      }
    })), m("div", {
      className: "PB-ImageField-controls"
    }, m("label", {
      className: "Button Button--primary PB-ImageField-uploadBtn"
    }, m("i", {
      className: "fas fa-cloud-arrow-up"
    }), ' ', this.uploading ? t('forum.image.uploading') : img ? t('forum.image.replace') : t('forum.image.upload'), m("input", {
      type: "file",
      accept: "image/*",
      disabled: this.uploading,
      onchange: e => {
        const file = e.target.files?.[0];
        if (file) this.upload(file);
        e.target.value = '';
      }
    })), img && m("button", {
      type: "button",
      className: "Button PB-ImageField-removeBtn",
      onclick: () => {
        this.broken = false;
        this.attrs.onChange(null);
      }
    }, m("i", {
      className: "fas fa-trash"
    }), " ", t('forum.image.remove'))), m("input", {
      type: "text",
      className: "FormControl PB-ImageField-url",
      placeholder: t('forum.image.paste_url'),
      value: urlValue,
      oninput: e => {
        this.broken = false;
        const v = e.target.value.trim();
        this.attrs.onChange(v || null);
      }
    }), this.error && m("div", {
      className: "PB-fieldError"
    }, this.error));
  }
  upload(file) {
    if (this.uploading) return;

    // fof/upload powers the /fof/upload endpoint and is an optional (suggested)
    // dependency. When it isn't installed the forum exposes no fof-upload.*
    // attributes — tell the admin what to install instead of letting the POST
    // fail with a generic error.
    if (app.forum.attribute('fof-upload.canUpload') === undefined) {
      this.error = extractText(t('forum.image.upload_unavailable'));
      m.redraw();
      return;
    }
    this.uploading = true;
    this.error = null;
    m.redraw();
    const body = new FormData();
    body.append('files[]', file);
    app.request({
      method: 'POST',
      url: `${app.forum.attribute('apiUrl')}/fof/upload`,
      serialize: raw => raw,
      body
    }).then(res => {
      const file0 = Array.isArray(res?.data) ? res.data[0] : res?.data;
      const attrs = file0?.attributes || {};
      const url = attrs.url || '';
      if (!url) throw new Error('No URL returned');
      const out = {
        url
      };
      if (attrs.thumbnailUrl) out.thumbnailUrl = attrs.thumbnailUrl;
      if (attrs.width) out.width = attrs.width;
      if (attrs.height) out.height = attrs.height;
      this.uploading = false;
      this.broken = false;
      this.attrs.onChange(out);
      m.redraw();
    }).catch(err => {
      this.uploading = false;
      this.error = err?.response?.errors?.[0]?.detail || t('forum.image.upload_error');
      m.redraw();
    });
  }
}
flarum.reg.add('ernestdefoe-page-builder', 'forum/components/ImageUploadInput', ImageUploadInput);